import { Container } from 'unstated';
import { isEmpty, isNumeric } from 'validator';

import { filterAllAttributesFromCategories } from '../../../../helpers/Categories/filterAllAttributesFromCategories';
import { validateEmptyLanguageObject } from '../../../../helpers/Validations/validateEmptyLanguageObject';
import { validateIncompleteLanguageObject } from '../../../../helpers/Validations/validateIncompleteLanguageObject';
import { filterUniqueUsedVisualValues } from '../../../../helpers/Variants/filterUniqueUsedVisualValues';
import { getUsedVisualAttribute } from '../../../../helpers/Variants/getUsedVisualAttribute';
import { validateEmptyValue } from '../../../../helpers/Validations/validateEmptyValue';
import { filterUniqueVisualCombinations } from '../../../../helpers/Variants/filterUniqueVisualCombinations';
import { filterAllAttributesFromVariants } from '../../../../helpers/Variants/filterAllAttributesFromVariants';

export interface IGeneralSettingsValidationErrors {
  title: string | undefined;
  name: string | undefined;
  // description: string | undefined;
  manufacturer: string | undefined;
  supplier: string | undefined;
  price: string | undefined;
  recommendedPrice: string | undefined;
  perex: string | undefined;
  // weight: string | undefined;
  // slug: string | undefined;
}

export interface IGeneralSettingsValidationWarnings {
  name: string | undefined;
  description: string | undefined;
  technicalSpecification: string | undefined;
  subtitle: string | undefined;
  // perex: string | undefined;
}

export interface IValidationErrors {
  generalSettings: IGeneralSettingsValidationErrors;
}

export interface IValidationWarnings {
  generalSettings: IGeneralSettingsValidationWarnings;
}

export interface IInputProduct extends GQL.IProduct {
  accessoriesToUpdate?: Array<GQL.IAccessoryInput>;
  manufacturerId: string | undefined;
  supplierId: string | undefined;
}

export interface IProductDetailState {
  product: IInputProduct;
  errors: IValidationErrors;
  warnings: IValidationWarnings;
  languages: Array<GQL.ILanguage>;
  currentLanguage: string;
  upserting: boolean;
}

export interface IProductDetailStateProps {
  product: GQL.IProduct;
  languages: Array<GQL.ILanguage>;
}

export class StateContainer extends Container<IProductDetailState> {
  constructor(props: IProductDetailStateProps) {
    super();

    this.state = {
      product: this.getProductOrDefault(props),
      errors: { generalSettings: {} } as IValidationErrors,
      warnings: { generalSettings: {} } as IValidationWarnings,
      languages: props.languages,
      currentLanguage: props.languages.map(l => l.value)[ 0 ],
      upserting: false
    };

    this.preValidate();
  }

  public async preValidate() {
    if (this.state.product.id != undefined) {
      await this.validateField('name', 'language');
      await this.validateField('subtitle', 'language');
      await this.validateField('description', 'language');
      await this.validateField('technicalSpecification', 'language');
      // await this.validateField('perex', 'language');
    }
  }

  public async onChange(key: string, val: any, validate?: boolean, language?: string) {
    const product = { ...this.state.product };

    if (language) {
      await this.setState({ product: { ...product, [ key ]: { ...this.state.product[ key ], [ language ]: val } } });
    } else {
      await this.setState({ product: { ...product, [ key ]: val } });
    }

    if (validate) {
      await this.validateField(key, language ? 'language' : 'regular');
    }
  }

  public async onChangeMany(pairs: Array<{ key: string; val: any; validate: boolean; language?: string }>) {
    for (const pair of pairs) {
      await this.onChange(pair.key, pair.val, pair.validate, pair.language);
    }
  }

  public async validate(): Promise<IValidationErrors> {
    const product = { ...this.state.product };
    const errors = { ...this.state.errors.generalSettings };
    const message = 'This field is required';

    const validateEmpty = (input: string) => {
      if (input == undefined || isEmpty(input)) {
        return message;
      } else {
        return undefined;
      }
    };

    const validateNumeric = (input: number) => {
      if (input == undefined || !isNumeric('' + input)) {
        return message;
      }

      return undefined;
    };

    errors.title = validateEmpty(product.title);
    errors.name = validateEmptyLanguageObject(product.name, message);
    // errors.description = validateEmptyLanguageObject(product.description, message);
    // errors.manufacturer = validateObjectChild(product.manufacturer, obj => !isEmpty(obj.id || ''));
    // errors.supplier = validateObjectChild(product.supplier, obj => !isEmpty(obj.id || ''));
    errors.supplier = validateEmpty(product.supplierId || '');
    errors.manufacturer = validateEmpty(product.manufacturerId || '');
    errors.price = validateNumeric(product.price || 0);
    errors.recommendedPrice = validateNumeric(product.recommendedPrice || 0);
    // errors.perex = validateEmptyLanguageObject(product.perex, message);
    // errors.weight = validateNumeric(product.weight);
    // errors.slug = validateEmptyLanguageObject(product.slug, message);

    await this.setState({ errors: { generalSettings: errors } });
    return { generalSettings: errors };
  }

  private async validateField(fieldName: string, type: 'language' | 'regular') {
    const { product } = this.state;

    if (type == 'language') {
      const messageForEmpty = validateEmptyLanguageObject(product[ fieldName ], 'This field is required');
      if (messageForEmpty) {
        await this.setState({ errors: { ...this.state.errors, [ fieldName ]: messageForEmpty } });
        return;
      }

      const messageForIncomplete = validateIncompleteLanguageObject(
        product[ fieldName ],
        this.state.languages.map(l => l.value),
        'Not all languages are filled'
      );

      await this.setState({
        warnings: {
          generalSettings: { ...this.state.warnings.generalSettings, [ fieldName ]: messageForIncomplete }
        },
        errors: { generalSettings: { ...this.state.errors.generalSettings, [ fieldName ]: messageForEmpty } }
      });
    } else {
      const message = validateEmptyValue(product[ fieldName ]);
      await this.setState({
        errors: { generalSettings: { ...this.state.errors.generalSettings, [ fieldName ]: message } }
      });
    }
  }

  public getUniqueUsedVisualValues() {
    return filterUniqueUsedVisualValues(
      this.state.product.variants || [],
      getUsedVisualAttribute(this.state.product.variants || [], this.getAllAttributes())
    );
  }

  public getUniqueVisualCombinations(allAttributes: Array<GQL.IAttribute>) {
    return filterUniqueVisualCombinations(
      this.state.product.variants || [],
      getUsedVisualAttribute(this.state.product.variants || [], this.getAllAttributesFromVariants(allAttributes))
    );
  }

  public getUsedVisualAttribute() {
    return getUsedVisualAttribute(this.state.product.variants || [], this.getAllAttributes());
  }

  /**
   * Vrací seznam použitých atributů ve variantách produktu
   *
   * @param {Array<GQL.IAttribute>} allAttributes - kompletní seznam všech atributů z DB
   * @returns {Array<GQL.IAttribute>}
   */
  public getAllAttributesFromVariants(allAttributes: Array<GQL.IAttribute>) {
    return filterAllAttributesFromVariants(this.state.product.variants || [], allAttributes);
  }

  public getAllAttributes() {
    return filterAllAttributesFromCategories(this.state.product.categories || []);
  }

  private getProductOrDefault(props: IProductDetailStateProps): IInputProduct {
    const product = ({ ...props.product } || {}) as IInputProduct;
    product.title = product.title || '';
    product.name = product.name || {};
    product.description = product.description || {};
    product.slug = product.slug || {};
    product.subtitle = product.subtitle || {};
    product.technicalSpecification = product.technicalSpecification || {};
    product.display = product.display || false;
    product.perex = product.perex || {};
    product.manufacturerId = (product.manufacturer || {}).id;
    product.supplierId = (product.supplier || {}).id;
    product.prices = product.prices || [];
    product.displayStatus = product.displayStatus || 0;
    // product.price = product.price || 0;
    // product.deliveryPriceAddition = product.deliveryPriceAddition || 0;
    // product.weight = product.weight || 0;
    product.status = product.status || 3;
    product.VATrate = product.VATrate || 15;
    // (product as any).accessoriesToUpdate = (product.accessories || []).map(a => {
    //     return {
    //         variantId: a.productVariant.id,
    //         valueId: a.value.id
    //     };
    // });

    // product.visual = product.visual
    product.sku = product.sku || null;

    // quick fix
    if (product.slug && typeof product.slug === 'string') {
      product.slug = {
        "cs": product.slug,
      };
    }

    return product;
  }
}



// WEBPACK FOOTER //
// ./src/scenes/Products/ProductDetail/StateContainer/StateContainer.tsx